<script setup>
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue';
import { Head, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

const { props } = usePage();
const siteInfo = ref(props.siteInformation);
console.log(siteInfo.value);

// Arabalar ve filtreler
const cars = ref([]);
const loading = ref(false);
const filters = reactive({
    price_min: '',
    price_max: '',
    year_min: '',
    year_max: '',
    fuel_type: '',
    gear_type: '',
    status: '',
    km_min: '',
    km_max: '',
    city: '',
    city_id: '',
    district_id: '',
    case_type: '',
    engine_power: '',
    engine_volume: '',
    color: '',
    is_exchange: '',
    is_serious_damage: '',
    is_guarantee: '',
    is_published: '',
    plate: '',
    chassis_number: '',
    engine_number: '',
    brand_id: '',
    model_id: '',
    version_id: '',
    packet_id: '',
    address: '',
    // boya değişen parça ve detaylar
    front_bumper: '',
    engine_hood: '',
    ceiling: '',
    right_front_fender: '',
    right_front_door: '',
    right_rear_door: '',
    right_rear_fender: '',
    left_front_fender: '',
    left_front_door: '',
    left_rear_door: '',
    left_rear_fender: '',
    trunk_lid: '',
    rear_bumper: '',
    front_bumper_detail: '',
    engine_hood_detail: '',
    ceiling_detail: '',
    right_front_fender_detail: '',
    right_front_door_detail: '',
    right_rear_door_detail: '',
    right_rear_fender_detail: '',
    left_front_fender_detail: '',
    left_front_door_detail: '',
    left_rear_door_detail: '',
    left_rear_fender_detail: '',
    trunk_lid_detail: '',
    rear_bumper_detail: '',
});

const fuelTypes = ['Benzinli', 'Benzin & LPG', 'Dizel', 'Hibrit', 'Elektrikli'];
const gearTypes = ['Manuel', 'Otomatik'];
const cities = ref([]);
const caseTypes = ['Sedan', 'Hatchback', 'SUV', 'Coupe', 'Cabrio', 'MPV', 'Pick-up', 'Diğer'];
const colors = ['Beyaz', 'Siyah', 'Gri', 'Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Turuncu', 'Kahverengi', 'Mor', 'Bej', 'Füme', 'Diğer'];

// Filtre toggle state
const showFilters = ref(false);

const sortBy = ref('created_at');
const sortDir = ref('desc');

// Görünüm modu (liste/grid)
const viewMode = ref('list'); // 'list' veya 'grid'

// Aktif navigasyon menüsü
const activeNav = ref('listings');

// Sıralama seçenekleri
const sortOptions = [
    { value: 'created_at_desc', label: 'En Yeni', sort_by: 'created_at', sort_dir: 'desc' },
    { value: 'created_at_asc', label: 'En Eski', sort_by: 'created_at', sort_dir: 'asc' },
    { value: 'price_asc', label: 'En Düşük Fiyat', sort_by: 'price', sort_dir: 'asc' },
    { value: 'price_desc', label: 'En Yüksek Fiyat', sort_by: 'price', sort_dir: 'desc' },
    { value: 'year_desc', label: 'En Yeni Model', sort_by: 'year', sort_dir: 'desc' },
    { value: 'year_asc', label: 'En Eski Model', sort_by: 'year', sort_dir: 'asc' },
    { value: 'kilometer_desc', label: 'En Az KM', sort_by: 'kilometer', sort_dir: 'desc' },
    { value: 'kilometer_asc', label: 'En Çok KM', sort_by: 'kilometer', sort_dir: 'asc' },
];

const selectedSort = ref('created_at_desc');

const fetchCities = async () => {
    // İlleri backend'den çekmek için (örnek endpoint, gerekirse değiştir)
    try {
        const res = await axios.get('/api/cities');
        cities.value = res.data;
    } catch (e) {
        cities.value = [];
    }
};
const meta = ref({ current_page: 1, last_page: 1, per_page: 16, total: 0 });
const page = ref(1);
const perPage = ref(16);

const fetchCars = async () => {
    loading.value = true;
    try {
        const params = { ...filters, page: page.value, per_page: perPage.value, sort_by: sortBy.value, sort_dir: sortDir.value };
        // Booleanlar için string "1"/"0" gönderiliyorsa düzelt

        const res = await axios.get('/cars/filtered', { params });
        cars.value = res.data.data || [];
        meta.value = res.data.meta || { current_page: 1, last_page: 1, per_page: 16, total: 0 };
    } catch (e) {
        cars.value = [];
        meta.value = { current_page: 1, last_page: 1, per_page: 16, total: 0 };
    } finally {
        loading.value = false;
    }
};

const handleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
    fetchCars();
};

const handleSortChange = (sortValue) => {
    const selectedOption = sortOptions.find((option) => option.value === sortValue);
    if (selectedOption) {
        sortBy.value = selectedOption.sort_by;
        sortDir.value = selectedOption.sort_dir;
        fetchCars();
    }
};

function slugify(text) {
    return text
        .toString()
        .toLowerCase()
        .replace(/ç/g, 'c')
        .replace(/ğ/g, 'g')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ş/g, 's')
        .replace(/ü/g, 'u')
        .replace(/[^a-z0-9\s-]/g, '') // Harf, sayı, boşluk ve tire dışındakileri kaldır
        .replace(/\s+/g, '-') // Boşlukları tire yap
        .replace(/-+/g, '-') // Birden fazla tire varsa teke indir
        .replace(/^-+|-+$/g, ''); // Baştaki ve sondaki tireleri sil
}
const goToShow = (id, title) => {
    const slug = slugify(title);
    window.location.href = `/otomobil/${id}/${slug}`;
};

const resetFilters = () => {
    Object.keys(filters).forEach((key) => (filters[key] = ''));
    page.value = 1;
};

watch(
    filters,
    () => {
        page.value = 1;
        fetchCars();
    },
    { deep: true },
);
// Pagination watcher düzeltildi
watch(() => [page.value, perPage.value], fetchCars);

const decrementPage = () => {
    page.value = Math.max(1, meta.value.current_page - 1);
};
const incrementPage = () => {
    page.value = Math.min(meta.value.last_page, meta.value.current_page + 1);
};

onMounted(() => {
    fetchCities();
    // Başlangıç sıralamasını ayarla
    const initialSort = sortOptions.find((option) => option.value === selectedSort.value);
    if (initialSort) {
        sortBy.value = initialSort.sort_by;
        sortDir.value = initialSort.sort_dir;
    }
    fetchCars();
});

// Mobil tespiti için
const isMobile = ref(false);
const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
};
onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
});
onBeforeUnmount(() => {
    window.removeEventListener('resize', checkMobile);
});

const dashboard_title = ref('');

onMounted(() => {
    if (props.siteInformation.dashboard_title) {
        dashboard_title.value = props.siteInformation.dashboard_title;
    } else {
        if (props.siteInformation.title) {
            dashboard_title.value = props.siteInformation.title + ' - ' + 'Güvenle Otomobil Al';
        } else {
            dashboard_title.value = 'Güvenle Otomobil Al';
        }
    }
});
</script>

<template>
    <Head :title="dashboard_title" />
    <AuthenticatedLayout>
        <template #header>
            <h2 class="text-xl font-semibold leading-tight text-gray-800 dark:text-gray-200">Dashboard</h2>
        </template>

        <div class="mx-auto max-w-7xl">
            <!-- Banner ve Profil Bölümü -->
            <div class="mb-8 overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-blue-800">
                <!-- Banner Arka Plan -->
                <div class="relative h-48 bg-gradient-to-r from-blue-600 to-blue-800">
                    <div class="absolute inset-0 bg-black bg-opacity-20"></div>
                    <div class="absolute inset-0 bg-opacity-30 bg-gradient-to-r from-transparent to-blue-900"></div>

                    <!-- Profil Bilgileri -->
                    <div class="relative flex h-full items-center p-6">
                        <!-- Logo -->
                        <div class="mr-6 flex-shrink-0">
                            <div class="flex h-20 w-20 items-center justify-center rounded-lg bg-white p-0 shadow-lg md:h-24 md:w-24 lg:h-24 lg:w-24">
                                <img v-if="siteInfo.banner_logo_path" :src="siteInfo.banner_logo_path" alt="Logo" class="h-12 w-auto max-w-[120px] object-contain" />
                                <img v-else src="/public/assets/otoyosun_logo.png" alt="Logo" class="w-full" />
                            </div>
                        </div>

                        <!-- İşletme Bilgileri -->
                        <div class="flex-1 text-white">
                            <h1 class="text-xl font-bold md:text-3xl">{{ siteInfo.banner_content ?? '' }}</h1>
                        </div>

                        <!-- Premium Badge -->
                        <div class="absolute right-6 top-6">
                            <div class="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-400 text-xs font-bold text-yellow-900">4.yıl</div>
                        </div>

                        <!-- Mesaj Butonu -->
                        <a :href="'tel:' + siteInfo.phone_number">
                            <div class="ml-4">
                                <button class="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-lg transition-colors hover:bg-gray-100">Şimdi Ara</button>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            <!-- Üst Başlık ve Kontroller -->
            <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <!-- Sol taraf - Başlık -->
                <div class="flex items-center">
                    <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Tüm İlanlarımız ({{ meta.total }} ilan)</h1>
                </div>

                <!-- Sağ taraf - Kontroller -->
                <!-- Sağ taraf - Kontroller -->
                <div class="flex flex-col items-end gap-3 md:flex-col md:items-end lg:flex-row lg:items-center xl:flex-row xl:items-center">
                    <div class="flex w-full flex-row items-center justify-between gap-5 md:hidden md:flex-col lg:hidden lg:flex-col">
                        <!-- Görünüm -->
                        <div class="mt-1 flex items-center gap-2">
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Görünüm</span>
                            <div class="flex rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800">
                                <button @click="viewMode = 'list'" :class="`flex items-center justify-center rounded-md px-3 py-1.5 text-sm transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                    </svg>
                                </button>
                                <button @click="viewMode = 'grid'" :class="`flex items-center justify-center rounded-md px-3 py-1.5 text-sm transition-colors ${viewMode === 'grid' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Sıralama -->
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Sırala</span>
                            <select v-model="selectedSort" @change="handleSortChange(selectedSort)" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:w-auto">
                                <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Görünüm -->
                    <div class="mt-1 md:flex lg:flex hidden items-center gap-2">
                        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Görünüm</span>
                        <div class="flex rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800">
                            <button @click="viewMode = 'list'" :class="`flex items-center justify-center rounded-md px-3 py-1.5 text-sm transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`">
                                <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                            </button>
                            <button @click="viewMode = 'grid'" :class="`flex items-center justify-center rounded-md px-3 py-1.5 text-sm transition-colors ${viewMode === 'grid' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`">
                                <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Sıralama -->
                    <div class="items-center gap-2 lg:flex md:flex hidden">
                        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Sırala</span>
                        <select v-model="selectedSort" @change="handleSortChange(selectedSort)" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:w-auto">
                            <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>

                    <!-- Filtreleme -->
                    <div class="w-full">
                        <button @click="showFilters = !showFilters" class="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 sm:mt-0">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707l-7.414 7.414A1 1 0 0012 15v4a1 1 0 01-1 1h-2a1 1 0 01-1-1v-4a1 1 0 00-.293-.707L3.293 6.707A1 1 0 013 6V4z" />
                            </svg>
                            Filtrele
                        </button>
                    </div>
                </div>
            </div>

            <!-- Filtre Paneli -->
            <transition name="slide">
                <div v-if="showFilters" class="mb-8 rounded-xl bg-white p-6 shadow-lg dark:bg-gray-900">
                    <div class="mb-4 flex items-center justify-between">
                        <h3 class="text-lg font-bold tracking-tight text-gray-800 dark:text-gray-200">Filtrele</h3>
                        <button @click="resetFilters" class="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">Filtreleri Sıfırla</button>
                    </div>

                    <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        <!-- Fiyat -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Fiyat (TL)</label>
                            <div class="flex gap-2">
                                <input v-model="filters.price_min" type="number" placeholder="Min" class="input w-1/2" />
                                <input v-model="filters.price_max" type="number" placeholder="Max" class="input w-1/2" />
                            </div>
                        </div>

                        <!-- Yıl -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Yıl</label>
                            <div class="flex gap-2">
                                <input v-model="filters.year_min" type="number" placeholder="Min" class="input w-1/2" />
                                <input v-model="filters.year_max" type="number" placeholder="Max" class="input w-1/2" />
                            </div>
                        </div>

                        <!-- Kilometre -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Kilometre</label>
                            <div class="flex gap-2">
                                <input v-model="filters.km_min" type="number" placeholder="Min KM" class="input w-1/2" />
                                <input v-model="filters.km_max" type="number" placeholder="Max KM" class="input w-1/2" />
                            </div>
                        </div>

                        <!-- Yakıt Tipi -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Yakıt Tipi</label>
                            <select v-model="filters.fuel_type" class="input w-full">
                                <option value="">Tümü</option>
                                <option v-for="type in fuelTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </div>

                        <!-- Vites -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Vites</label>
                            <select v-model="filters.gear_type" class="input w-full">
                                <option value="">Tümü</option>
                                <option v-for="type in gearTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </div>

                        <!-- Kasa Tipi -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Kasa Tipi</label>
                            <select v-model="filters.case_type" class="input w-full">
                                <option value="">Tümü</option>
                                <option v-for="type in caseTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </div>

                        <!-- Renk -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Renk</label>
                            <select v-model="filters.color" class="input w-full">
                                <option value="">Tümü</option>
                                <option v-for="color in colors" :key="color" :value="color">{{ color }}</option>
                            </select>
                        </div>

                        <!-- İl -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">İl</label>
                            <select v-model="filters.city" class="input w-full">
                                <option value="">Tümü</option>
                                <option v-for="city in cities" :key="city.id" :value="city.name">{{ city.name }}</option>
                            </select>
                        </div>

                        <!-- Takaslı -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Takaslı</label>
                            <select v-model="filters.is_exchange" class="input w-full">
                                <option value="">Tümü</option>
                                <option value="1">Evet</option>
                                <option value="0">Hayır</option>
                            </select>
                        </div>

                        <!-- Ağır Hasarlı -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Ağır Hasarlı</label>
                            <select v-model="filters.is_serious_damage" class="input w-full">
                                <option value="">Tümü</option>
                                <option value="1">Evet</option>
                                <option value="0">Hayır</option>
                            </select>
                        </div>

                        <!-- Garanti -->
                        <div>
                            <label class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Garanti</label>
                            <select v-model="filters.is_guarantee" class="input w-full">
                                <option value="">Tümü</option>
                                <option value="1">Evet</option>
                                <option value="0">Hayır</option>
                            </select>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Arabalar Listesi/Grid -->
            <div class="rounded-xl bg-white shadow-lg dark:bg-gray-900">
                <div v-if="loading" class="flex flex-col items-center justify-center py-16">
                    <svg class="mb-4 h-10 w-10 animate-spin text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span class="text-lg text-gray-500">Yükleniyor...</span>
                </div>

                <div v-else-if="cars.length === 0" class="flex flex-col items-center justify-center py-16 text-gray-500">
                    <svg class="mb-2 h-12 w-12" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75 5.25 5.25m0 0v4.5m0-4.5h4.5M14.25 14.25l4.5 4.5m0 0v-4.5m0 4.5h-4.5" />
                    </svg>
                    <span class="text-lg">Kriterlere uygun araç bulunamadı.</span>
                </div>

                <!-- Liste Görünümü -->
                <div v-else-if="viewMode === 'list'" class="overflow-x-auto">
                    <Table class="min-w-full divide-y divide-gray-200 bg-white text-xs dark:bg-gray-900 md:text-sm">
                        <TableHeader class="bg-gray-50 dark:bg-gray-800">
                            <TableRow class="border-b-2 border-gray-200 dark:border-gray-700">
                                <TableHead class="p-3 text-left font-bold text-gray-700 dark:text-gray-200"></TableHead>
                                <TableHead @click="() => handleSort('brand_id')" :class="'cursor-pointer select-none p-3 text-left font-bold text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 ' + (sortBy.value === 'brand_id' ? 'text-blue-600 dark:text-blue-400' : '')"> Marka </TableHead>
                                <TableHead v-show="!isMobile" @click="() => handleSort('model_id')" :class="'cursor-pointer select-none p-3 text-left font-bold text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 ' + (sortBy.value === 'model_id' ? 'text-blue-600 dark:text-blue-400' : '')"> Model </TableHead>
                                <TableHead @click="() => handleSort('year')" :class="'cursor-pointer select-none p-3 text-left font-bold text-gray-700 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ' + (sortBy.value === 'year' ? 'text-blue-700 dark:text-blue-300' : '')"> Yıl </TableHead>
                                <TableHead @click="() => handleSort('kilometer')" :class="'cursor-pointer select-none p-3 text-left font-bold text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 ' + (sortBy.value === 'kilometer' ? 'text-blue-600 dark:text-blue-400' : '')"> KM </TableHead>
                                <TableHead v-show="!isMobile" class="p-3 text-left font-bold text-gray-700 dark:text-gray-200"> Motor Gücü </TableHead>
                                <TableHead v-show="!isMobile" @click="() => handleSort('color')" :class="'cursor-pointer select-none p-3 text-left font-bold text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 ' + (sortBy.value === 'color' ? 'text-blue-600 dark:text-blue-400' : '')"> Renk </TableHead>
                                <TableHead @click="() => handleSort('price')" :class="'cursor-pointer select-none p-3 text-left font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ' + (sortBy.value === 'price' ? 'text-blue-700 dark:text-blue-300' : '')"> Fiyat </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                            <TableRow v-for="car in cars" :key="car.id" class="cursor-pointer transition-all duration-200 hover:bg-blue-50 dark:hover:bg-gray-800" @click="() => goToShow(car.id, car.title)">
                                <TableCell class="p-3 align-middle">
                                    <div class="relative">
                                        <img :src="car.showcase_image_url || '/public/carImages/Hyundai.jpeg'" alt="Araba" class="h-24 w-32 rounded-lg object-cover shadow-md md:h-28 md:w-40" />
                                    </div>
                                </TableCell>

                                <TableCell class="p-3 align-middle">
                                    <div class="flex items-center">
                                        <template v-if="!isMobile">
                                            <img v-if="car.brand?.attachments?.length > 0" :src="`/${car.brand.attachments[0].path}`" alt="Marka Görseli" class="mr-3 h-8 w-8 object-contain" />
                                        </template>
                                        <span class="font-semibold text-gray-800 dark:text-gray-100">{{ car.brand?.name }}</span>
                                    </div>
                                </TableCell>
                                <TableCell v-show="!isMobile" class="p-3 align-middle">
                                    <span class="font-medium text-gray-700 dark:text-gray-200">{{ car.model?.name }}</span>
                                </TableCell>
                                <TableCell class="p-3 align-middle">
                                    <span class="font-semibold text-gray-800 dark:text-gray-100">{{ car.year }}</span>
                                </TableCell>
                                <TableCell class="p-3 align-middle">
                                    <span class="font-medium text-gray-700 dark:text-gray-200">{{ car.kilometer?.toLocaleString() }}</span>
                                </TableCell>
                                <TableCell v-show="!isMobile" class="p-3 align-middle">
                                    <span class="font-medium text-gray-700 dark:text-gray-200">{{ car.engine_power || 'N/A' }} hp</span>
                                </TableCell>
                                <TableCell v-show="!isMobile" class="p-3 align-middle">
                                    <span class="font-medium text-gray-700 dark:text-gray-200">{{ car.color }}</span>
                                </TableCell>
                                <TableCell class="p-3 align-middle">
                                    <span class="text-medium font-bold text-blue-800">{{ car.price?.toLocaleString() }} TL</span>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>

                <!-- Grid Görünümü -->
                <div v-else class="p-6">
                    <div class="grid grid-cols-1 gap-6 md:grid-cols-4 lg:grid-cols-4">
                        <div v-for="car in cars" :key="car.id" @click="() => goToShow(car.id, car.title)" class="group cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md transition-all duration-200 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800">
                            <!-- Araba Görseli -->
                            <div class="relative h-48 overflow-hidden">
                                <img :src="car.showcase_image_url || '/public/carImages/Hyundai.jpeg'" alt="Araba" class="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                                <!-- Marka Logo -->
                                <div class="absolute bottom-2 left-2">
                                    <img v-if="car.brand?.attachments?.length > 0" :src="`/${car.brand.attachments[0].path}`" alt="Marka Görseli" class="h-8 w-8 rounded-full bg-white p-1 shadow-md" />
                                </div>
                            </div>

                            <!-- Araba Bilgileri -->
                            <div class="p-4">
                                <div class="mb-2">
                                    <h3 class="font-semibold text-gray-900 dark:text-gray-100">{{ car.brand?.name }} {{ car.model?.name }}</h3>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">{{ car.year }} • {{ car.kilometer?.toLocaleString() }} KM</p>
                                </div>

                                <div class="mb-3 flex items-center justify-between text-sm">
                                    <span class="text-gray-600 dark:text-gray-400">{{ car.engine_power || 'N/A' }} hp</span>
                                    <span class="text-gray-600 dark:text-gray-400">{{ car.color }}</span>
                                </div>

                                <div class="text-right">
                                    <span class="text-lg font-bold text-blue-800">{{ car.price?.toLocaleString() }} TL</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div class="mt-6 flex justify-center">
                <div class="flex flex-wrap items-center justify-center gap-2">
                    <button :disabled="meta.current_page === 1" @click="decrementPage" class="rounded-full bg-gray-200 px-4 py-2 text-lg font-bold disabled:opacity-50 dark:bg-gray-800">&lt;</button>
                    <span class="flex items-center px-4 py-2 text-base font-semibold">{{ meta.current_page }} / {{ meta.last_page }}</span>
                    <button :disabled="meta.current_page === meta.last_page" @click="incrementPage" class="rounded-full bg-gray-200 px-4 py-2 text-lg font-bold disabled:opacity-50 dark:bg-gray-800">&gt;</button>
                </div>
            </div>
        </div>
    </AuthenticatedLayout>
</template>

<style scoped>
.input {
    @apply rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100;
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}

@media (max-width: 768px) {
    .min-w-full {
        min-width: 700px;
    }
    .overflow-x-auto {
        -webkit-overflow-scrolling: touch;
    }
    th,
    td {
        padding: 0.25rem !important;
        font-size: 12px !important;
    }
}

.writing-mode-vertical {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
}

/* Mobil cihazlarda butonun konumunu ayarla */
@media (max-width: 768px) {
    .fixed.right-4 {
        right: 0.5rem;
    }
}
</style>
